import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { schema as schemaYup } from './yupCreditCardFields';
import React, { ReactElement, useEffect } from 'react';
import { CreditCardDTO } from '@/data/DTOs/schedule/CreditCardDTO';

export interface CreditCardFieldsInterface {
  nameOnCard: string;
  creditCardNumber: string;
  date: Date;
  csc: number;
}

interface RegisterCreditCardControllerInterface {
  registerCreditCard: CreditCardDTO;
  setRegisterCreditCard: (creditCard: CreditCardDTO) => void;
  date: Date | null;
  setDate: (date: Date | null) => void;
  forceReset: boolean;
  setForceReset: (forceReset: boolean) => void;
}

export const RegisterCreditCardController = ({
  registerCreditCard,
  setDate,
  setRegisterCreditCard,
  forceReset,
  setForceReset,
}: RegisterCreditCardControllerInterface) => {
  const schema = schemaYup();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
    trigger,
  } = useForm<any>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (forceReset) {
      reset({
        nameOnCard: registerCreditCard.nameOnCard,
        creditCardNumber: registerCreditCard.creditCardNumber,
        date: undefined,
        csc: registerCreditCard.csc,
      });
      setForceReset(false);
    }
  }, [forceReset]);

  const getCreditCardFieldValue = (
    creditCard: CreditCardDTO,
    fieldName: string
  ): string => {
    if (fieldName in creditCard) {
      const value = creditCard[fieldName as keyof CreditCardDTO];
      return value !== undefined ? String(value) : '';
    }
    return 'date';
  };

  const controlledTextView = (
    children: ReactElement,
    fieldName: 'nameOnCard' | 'creditCardNumber' | 'date'
  ) => {
    return (
      <Controller
        name={fieldName}
        control={control}
        defaultValue={getCreditCardFieldValue(registerCreditCard, fieldName)}
        render={({ field }) =>
          React.cloneElement(children, {
            onValueChange: (value: string) => {
              setRegisterCreditCard({
                ...registerCreditCard,
                [fieldName]: value,
              });
              field.onChange(value);
            },
            onChange: (value: Date | null) => {
              if (value == null) {
                setDate(null);
                return;
              }
              if (!isNaN(value.getTime())) setDate(value);
              field.onChange(value);
            },
            slotProps: {
              textField: {
                color: 'focus',
                error: !!errors[fieldName],
                helperText: errors[fieldName]?.message ?? '',
              },
            },
            error: !!errors[fieldName],
            helperText: errors[fieldName]?.message,
          })
        }
      />
    );
  };

  return {
    errors,
    control,
    isValid,
    controlledTextView,
    handleSubmit,
    trigger,
  };
};
